/**
 * GalleryListView — pure render component for the gallery item list/tiles.
 *
 * iter-121.47 — redesigned to match the new Gallery page mockup:
 *   • List view  → modern table with icon plate + type chip + workspace +
 *                  relative time + actions overflow.
 *   • Tile view  → rounded card grid with icon plate header.
 *
 * Every previously-shipped data-testid is preserved so existing tests
 * continue to pass:
 *   - gallery-list (list table wrapper)
 *   - gallery-row-{id}
 *   - gallery-select-all / gallery-select-{id} / gallery-tile-select-{id}
 *   - gallery-shared-badge-{id} / gallery-shared-by-{id}
 *   - gallery-share-{id} / gallery-delete-{id}
 *   - gallery-item-{id} (tile)
 *   - gallery-empty
 *
 * New data-testids for the redesign:
 *   - gallery-actions-{id} (3-dot menu trigger)
 *   - gallery-icon-{id}    (icon plate)
 */
import React from "react";
import {
    BarChart2, Sparkles, Table as TableIcon, Trash2, Images, UserPlus,
    BarChart3, FileText, FolderClosed, MoreVertical,
} from "lucide-react";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

// ─── icon / plate registry per kind ────────────────────────────────
const KIND_PLATE = {
    kpi:     { Icon: BarChart3, plate: "bg-violet-100 text-violet-600",  label: "KPI" },
    chart:   { Icon: BarChart2, plate: "bg-emerald-100 text-emerald-600", label: "Chart" },
    table:   { Icon: TableIcon, plate: "bg-sky-100 text-sky-600",         label: "Table" },
    insight: { Icon: Sparkles,  plate: "bg-amber-100 text-amber-600",     label: "Insight" },
};
function plateFor(kind) {
    return KIND_PLATE[kind] || { Icon: FileText, plate: "bg-muted text-muted-foreground", label: kind || "Asset" };
}

// Re-export for back-compat with callers (existing imports may exist).
export const KIND_ICON = {
    kpi: BarChart3, chart: BarChart2, table: TableIcon, insight: Sparkles,
};

// ─── time helpers ───────────────────────────────────────────────────
function relativeTime(iso) {
    if (!iso) return "—";
    try {
        const then = new Date(iso).getTime();
        if (Number.isNaN(then)) return "—";
        const diff = Date.now() - then;
        const s = Math.max(0, Math.floor(diff / 1000));
        if (s < 60) return "just now";
        const m = Math.floor(s / 60);
        if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
        const d = Math.floor(h / 24);
        if (d < 7)  return `${d} day${d === 1 ? "" : "s"} ago`;
        const w = Math.floor(d / 7);
        if (w < 5)  return `${w} week${w === 1 ? "" : "s"} ago`;
        const mo = Math.floor(d / 30);
        if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
        const y = Math.floor(d / 365);
        return `${y} year${y === 1 ? "" : "s"} ago`;
    } catch { return "—"; }
}
function absoluteTime(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        // e.g. "May 21, 2026, 10:30 AM"
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
             + ", "
             + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
}

function workspaceName(item, ctx) {
    const id = item.workspace_id;
    if (!id) return "—";
    const w = (ctx.workspaces || []).find((x) => x.id === id);
    return w?.name || "Workspace";
}

// ─── list (table) view ──────────────────────────────────────────────
function GalleryDetailsTable({ ctx }) {
    const { filtered, allFiltered, isSuperAdmin, selectedIds, setPreview, toggleOne, toggleAllFiltered, canShare, canDelete, setShareItem, del } = ctx;
    // Select All spans every filtered item across pages, not just this page.
    const scope = allFiltered || filtered;
    const allChecked = scope.length > 0 && scope.every((i) => selectedIds.has(i.id));
    const someChecked = scope.some((i) => selectedIds.has(i.id)) && !allChecked;
    return (
        <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm" data-testid="gallery-list">
            <table className="min-w-[920px] w-full text-sm">
                <thead className="bg-muted/30">
                    <tr className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <th className="px-4 py-3 w-10 text-left">
                            {isSuperAdmin ? (
                                <input
                                    type="checkbox"
                                    checked={allChecked}
                                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                                    onChange={() => toggleAllFiltered(scope)}
                                    className="h-3.5 w-3.5 cursor-pointer accent-primary"
                                    data-testid="gallery-select-all"
                                    aria-label="Select all matching gallery items" />
                            ) : null}
                        </th>
                        <th className="px-3 py-3 text-left">Name</th>
                        <th className="px-3 py-3 text-left w-28">Type</th>
                        <th className="px-3 py-3 text-left">Workspace</th>
                        <th className="px-3 py-3 text-left w-44">Last modified</th>
                        <th className="px-3 py-3 text-left w-32">Tags</th>
                        <th className="px-3 py-3 text-right w-20">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((i) => {
                        const { Icon, plate, label } = plateFor(i.kind);
                        return (
                            <tr
                                key={i.id}
                                className={`border-t border-border transition-colors hover:bg-muted/40 cursor-pointer ${i._shared ? "bg-primary/5" : ""}`}
                                onClick={() => setPreview(i)}
                                data-testid={`gallery-row-${i.id}`}
                            >
                                <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                                    {isSuperAdmin ? (
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(i.id)}
                                            onChange={() => toggleOne(i.id)}
                                            className="h-3.5 w-3.5 cursor-pointer accent-primary"
                                            data-testid={`gallery-select-${i.id}`}
                                            aria-label={`Select ${i.name}`} />
                                    ) : null}
                                </td>
                                <td className="px-3 py-3">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${plate}`} data-testid={`gallery-icon-${i.id}`}>
                                            <Icon className="h-4 w-4" strokeWidth={1.75} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="truncate font-medium text-foreground">{i.name}</span>
                                                {i._shared && (
                                                    <Badge variant="outline" className="font-mono text-[9px] uppercase border-primary/40 text-primary" data-testid={`gallery-shared-badge-${i.id}`}>
                                                        Shared · {i._share_access}
                                                    </Badge>
                                                )}
                                            </div>
                                            {i._shared && (
                                                <div className="text-[10px] text-primary/80 font-mono mt-0.5" data-testid={`gallery-shared-by-${i.id}`}>
                                                    by {i._shared_by_name}{i._source_org_name ? ` · ${i._source_org_name}` : ""}
                                                </div>
                                            )}
                                            {!i._shared && i.description && (
                                                <div className="truncate text-[11px] text-muted-foreground">{i.description}</div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-3 py-3">
                                    <Badge variant="secondary" className="rounded-md bg-muted text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</Badge>
                                </td>
                                <td className="px-3 py-3 text-sm text-muted-foreground">
                                    <div className="inline-flex items-center gap-1.5">
                                        <FolderClosed className="h-3.5 w-3.5 text-muted-foreground/70" />
                                        <span className="truncate">{workspaceName(i, ctx)}</span>
                                    </div>
                                </td>
                                <td className="px-3 py-3 text-sm">
                                    <div className="text-foreground">{relativeTime(i.created_at)}</div>
                                    <div className="text-[10px] text-muted-foreground">{absoluteTime(i.created_at)}</div>
                                </td>
                                <td className="px-3 py-3 text-xs text-muted-foreground truncate">
                                    {(i.tags || []).slice(0, 3).map((t) => `#${t}`).join(" ") || "—"}
                                </td>
                                <td className="px-3 py-3 text-right">
                                    <RowActions item={i} ctx={ctx} canShare={canShare} canDelete={canDelete} setShareItem={setShareItem} del={del} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function RowActions({ item, ctx, canShare, canDelete, setShareItem, del }) {
    const hasShare = canShare(item);
    const hasDelete = canDelete(item);
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    data-testid={`gallery-actions-${item.id}`}
                    aria-label="Open actions menu"
                >
                    <MoreVertical className="h-4 w-4" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); ctx.setPreview(item); }} data-testid={`gallery-preview-${item.id}`}>
                    Preview
                </DropdownMenuItem>
                {hasShare && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShareItem(item); }} data-testid={`gallery-share-${item.id}`}>
                        <UserPlus className="mr-2 h-3.5 w-3.5" /> Share
                    </DropdownMenuItem>
                )}
                {hasDelete && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); del(item); }}
                            className="text-destructive focus:text-destructive"
                            data-testid={`gallery-delete-${item.id}`}
                        >
                            <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// ─── tile/grid view ─────────────────────────────────────────────────
function GalleryTiles({ ctx }) {
    const { filtered, isSuperAdmin, selectedIds, setPreview, toggleOne, canShare, canDelete, setShareItem, del } = ctx;
    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((i) => {
                const { Icon, plate, label } = plateFor(i.kind);
                const isSel = selectedIds.has(i.id);
                return (
                    <div
                        key={i.id}
                        onClick={() => setPreview(i)}
                        className={`group relative cursor-pointer rounded-xl border bg-card p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${
                            i._shared ? "border-primary/40" : "border-border"
                        } ${isSel ? "ring-2 ring-primary" : ""}`}
                        data-testid={`gallery-item-${i.id}`}
                    >
                        {isSuperAdmin && (
                            <input
                                type="checkbox"
                                checked={isSel}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleOne(i.id)}
                                className="absolute top-3 left-3 h-4 w-4 cursor-pointer accent-primary z-10"
                                data-testid={`gallery-tile-select-${i.id}`}
                                aria-label={`Select ${i.name}`} />
                        )}

                        <div className="flex items-start justify-between">
                            <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${plate}`} data-testid={`gallery-icon-${i.id}`}>
                                <Icon className="h-5 w-5" strokeWidth={1.75} />
                            </div>
                            <div className="flex items-center gap-1">
                                {i._shared && (
                                    <Badge variant="outline" className="font-mono text-[9px] uppercase border-primary/40 text-primary" title={`${i._share_access?.toUpperCase?.()} access`} data-testid={`gallery-shared-badge-${i.id}`}>
                                        <UserPlus className="mr-1 h-2.5 w-2.5" /> {i._share_access || "shared"}
                                    </Badge>
                                )}
                                <Badge variant="secondary" className="rounded-md bg-muted text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    {label}
                                </Badge>
                            </div>
                        </div>

                        <div className="mt-3 text-sm font-semibold leading-tight text-foreground truncate">{i.name}</div>
                        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <FolderClosed className="h-3 w-3" />
                            <span className="truncate">{workspaceName(i, ctx)}</span>
                        </div>
                        {i._shared && (
                            <div className="text-[11px] text-primary/80 mt-1.5 font-mono" data-testid={`gallery-shared-by-${i.id}`}>
                                by <span className="font-semibold">{i._shared_by_name}</span>
                                {i._source_org_name && <span className="text-muted-foreground"> · {i._source_org_name}</span>}
                            </div>
                        )}
                        {i.description && <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{i.description}</div>}

                        <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-2">
                            <div className="text-[11px] text-muted-foreground">{relativeTime(i.created_at)}</div>
                            <RowActions item={i} ctx={ctx} canShare={canShare} canDelete={canDelete} setShareItem={setShareItem} del={del} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ─── empty state ────────────────────────────────────────────────────
function GalleryEmpty() {
    return (
        <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 text-center text-sm text-muted-foreground" data-testid="gallery-empty">
            <Images className="mx-auto mb-3 h-10 w-10 opacity-30" />
            No assets match your filters.
        </div>
    );
}

// ─── default export ─────────────────────────────────────────────────
export default function GalleryListView({ ctx }) {
    const { filtered, viewMode } = ctx;
    if (filtered.length === 0) return <GalleryEmpty />;
    return (viewMode === "details" || viewMode === "list")
        ? <GalleryDetailsTable ctx={ctx} />
        : <GalleryTiles ctx={ctx} />;
}
